import { CommonModule } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { take } from 'rxjs';
import { Table } from '../../../shared/table/table';
import { Popup } from '../../../../core/shared/components/popup/popup';
import { AppIcons } from '../../../../core/icons/fa-icons';
import { DynamicFieldConfig } from '../../../../core/shared/dynamic-form/form-config.model';
import { PKL_GRP } from '../../../../core/shared/models/picklist.model';
import { PickListService } from '../../../../core/shared/services/picklist.service';
import { TableAction, TableColumn } from '../../../shared/models/table-models';
import { Setup } from '../../shared/service/setup';
import { FormsModule } from '@angular/forms';
import { BackButton } from "../../../shared/buttons/back-button";


@Component({
  selector: 'app-financial-data-value',
  standalone: true,
  imports: [TranslatePipe, CommonModule, Table, Popup, FormsModule, BackButton],
  templateUrl: './financial-data-value.html',
  styleUrl: './financial-data-value.css',
})
export class FinancialDataValue implements OnInit {
  icons = AppIcons;

  fncTypSeq!: number;

  list: any[] = [];
  columns: TableColumn[] = [];
  fields: DynamicFieldConfig[] = [];

  showPopup = false;
  isEditMode = false;
  formData: any = {};

  showDeletePopup = false;
  deleteMessage = '';
  selectedRow: any = null;

  constructor(
    private translate: TranslateService,
    private route: ActivatedRoute,
    private setup: Setup,
    private toastr: ToastrService,
    private picklist: PickListService
  ) {}

  ngOnInit(): void {
    this.fncTypSeq = Number(this.route.snapshot.paramMap.get('id'));
    this.loadPicklists();
  }

  loadPicklists(): void {
    this.picklist.loadCodes([PKL_GRP.COMMON_STATUS]);

    this.picklist.picklists$.pipe(take(1)).subscribe(() => {
      this.buildFields();
      this.buildColumns();
      this.loadData();
    });
  }

  buildFields(): void {
    this.fields = [
      {
        key: 'fncValSeq',
        label: 'SETUP.ID',
        type: 'text',
        hidden: true,
      },
      {
        key: 'description',
        label: 'COMMON.DESCRIPTION',
        type: 'textarea',
        validations: { required: true },
      },
      {
        key: 'sortOrdr',
        label: 'SETUP.SORT_ORDER',
        type: 'number',
        validations: { required: true },
      },
      {
        key: 'status',
        label: 'COMMON.STATUS',
        type: 'select',
        options$: this.picklist.getOptionsSync(PKL_GRP.COMMON_STATUS),
        validations: { required: true },
      },
    ];
  }

  buildColumns(): void {
    this.columns = [
      { key: 'fncValSeq', label: 'SETUP.ID' },
      { key: 'description', label: 'COMMON.DESCRIPTION' },
      { key: 'sortOrdr', label: 'SETUP.SORT_ORDER' },
      {
        key: 'status',
        label: 'COMMON.STATUS',
        type: 'badge',
        map: {
          706: 'COMMON.ACTIVE',
          707: 'COMMON.INACTIVE',
        },
        badgeMap: {
          706: 'status-active',
          707: 'status-inactive',
        },
      },
    ];
  }

  actions: TableAction[] = [
    {
      icon: this.icons.edit,
      tooltip: 'COMMON.EDIT',
      class: 'action-edit',
      onClick: (row) => this.openPopup(row),
    },
    {
      icon: this.icons.delete,
      tooltip: 'COMMON.DELETE',
      class: 'action-delete',
      onClick: (row) => this.openDeletePopup(row),
    },
  ];

  // ================= DELETE CONFIRM =================
  openDeletePopup(row: any) {
    this.selectedRow = row;
    this.deleteMessage = this.translate.instant('SETUP.CONFIRM_DELETE_NAME', { name: row.description });
    this.showDeletePopup = true;
  }

  confirmDelete(): void {
    if (!this.selectedRow) return;
    this.delete(this.selectedRow);
    this.selectedRow = null;
  }

loadData(): void {
  this.setup.getFinancialTypeValues(this.fncTypSeq).subscribe({
    next: (res: any[]) => {
      this.list = (res || [])
        .filter((item) => item.delFlg !== 1)
        .map((item) => ({
          fncValSeq: item.fncValSeq,
          fncTyp: item.fncTyp,
          description: item.dscr,
          sortOrdr: item.sortOrdr,
          actvFlg: item.actvFlg,
          status: item.actvFlg ? 706 : 707,
        }));
    },
    error: () => this.toastr.error(this.translate.instant('SETUP.FIN_DATA.LOAD_VALUES_FAILED')),
  });
}

  openPopup(data?: any): void {
    this.showPopup = true;

    if (data) {
      this.isEditMode = true;

      this.formData = {
        fncValSeq: data.fncValSeq,
        description: data.description,
        sortOrdr: data.sortOrdr,
        status: data.status,
      };
    } else {
      this.isEditMode = false;

      this.formData = {
        fncValSeq: null,
        description: '',
        sortOrdr: this.list.length + 1,
        status: this.getDefaultStatus(),
      };
    }
  }

  getDefaultStatus(): number {
    const options = this.picklist.getOptionsSync(PKL_GRP.COMMON_STATUS);
    return options?.[0]?.value || 706;
  }

  handleSave = (form: any) => {
    const payload = {
      fncValSeq: form.fncValSeq ?? null,
      fncTyp: this.fncTypSeq,
      actvFlg: Number(form.status) === 706,
      dscr: form.description,
      sortOrdr: Number(form.sortOrdr) || 1,
      delFlg: 0,
    };

    return this.setup.saveFinancialTypeValue(payload).subscribe({
      next: () => {
        this.toastr.success(
          this.translate.instant(this.isEditMode
            ? 'SETUP.FIN_DATA.VALUE_UPDATED'
            : 'SETUP.FIN_DATA.VALUE_CREATED')
        );
        this.showPopup = false;
        this.loadData();
      },
      error: () => this.toastr.error(this.translate.instant('SETUP.SAVE_FAILED')),
    });
  };

 delete(row: any): void {
  const payload = {
    fncValSeq: row.fncValSeq,
    fncTyp: this.fncTypSeq,
    actvFlg: Number(row.status) === 706,
    dscr: row.description,
    sortOrdr: Number(row.sortOrdr) || 1,
    delFlg: 1,
  };

  this.setup.saveFinancialTypeValue(payload).subscribe({
    next: () => {
      this.toastr.success(this.translate.instant('SETUP.DELETED_SUCCESSFULLY'));

      this.list = this.list.filter(
        (item) => item.fncValSeq !== row.fncValSeq
      );
    },
    error: () => this.toastr.error(this.translate.instant('SETUP.DELETE_FAILED')),
  });
}
}